import React from 'react';
import { useDevice } from '@opendoor/bricks/hooks/useMedia';
import { Box, BoxProps, Flex, Text } from '@opendoor/bricks/core';

import { getHubORVAScheduleRoute, getHubSSVAUploadRoute } from 'shared/helpers/routes';
import { LayoutContent } from 'platform/components/layout/Layout';
import { SwitchVaCard } from 'sellDirect/components/va/SwitchVaCard';
import { TwoColumnLayout } from 'sellDirect/components/layout/TwoColumnLayout';
import {
  SSVA_TARGETING_ORVA_DESCRIPTIONS,
  VAChecklist,
} from 'sellDirect/components/homeWalkthrough/VAChecklist';

interface VALayoutProps {
  /** What VA experience to display */
  vaExperience: 'ssva' | 'orva';
  /** Hide switch card */
  hideSwitchCard?: boolean;
}

/**
 * Reusable layout for Virtual Assessment pages
 * component-test-check-ignore
 */
export const VALayout: React.FC<VALayoutProps> = ({
  children,
  vaExperience,
  hideSwitchCard = false,
}) => {
  const { isMobile } = useDevice();
  const isSSVA = vaExperience === 'ssva';

  const title = isSSVA ? 'Share videos' : 'Book a video call';
  const pillText = isSSVA ? 'Self guided' : 'Live expert help';

  const rightComponent = !hideSwitchCard && (
    <SwitchVaCard
      analyticsName={isSSVA ? 'switch-to-orva' : 'switch-to-ssva'}
      aria-label={
        isSSVA ? 'Get help from an expert?' : 'Switch to a self serve virtual walkthrough'
      }
      description={isSSVA ? 'Want live help from an expert?' : 'Don’t want to wait for a call?'}
      to={isSSVA ? getHubORVAScheduleRoute() : getHubSSVAUploadRoute()}
      illustrationName={isSSVA ? 'Human' : 'Video Walkthrough'}
    >
      {isSSVA ? 'Switch to a live video call' : 'Share videos instead'}
    </SwitchVaCard>
  );

  const items = isSSVA
    ? [
        `We’ll show you what to do${!isMobile ? ', complete on your own time' : ''}`,
        'A local-market expert will review it all',
        'See a final offer in a few days',
      ]
    : SSVA_TARGETING_ORVA_DESCRIPTIONS;

  return (
    <LayoutContent maxWidth="100%" px={[0, 0, 7]}>
      <TwoColumnLayout
        rightComponent={!isMobile && rightComponent}
        hideRightColumn={hideSwitchCard}
      >
        <Flex flexDirection="column" gridGap={[5, 5, 7]}>
          {/* Title */}
          <Flex flexDirection="column" px={[7, 7, 0]}>
            {pillText && (
              <Pill alignSelf="start" mt={[0, 0, 8]}>
                {pillText}
              </Pill>
            )}
            <Text
              as="h2"
              mt="3"
              width={'100%'}
              fontSize={['s4', null, 's5']}
              fontWeight="bold"
              lineHeight={['s4', null, 's5']}
              alignSelf={['center', 'center', 'center', 'start']}
            >
              {title}
            </Text>
          </Flex>

          {/* Checklist */}
          <Flex flexDir="column" gridGap={5} px={[7, 7, 0]}>
            <VAChecklist items={items} />

            {isMobile && rightComponent}
          </Flex>

          {/* Content */}
          <Box mt={[2, 2, 0]}>{children}</Box>
        </Flex>
      </TwoColumnLayout>
    </LayoutContent>
  );
};

const Pill: React.FC<BoxProps> = ({ children, ...boxProps }) => (
  <Box bg="neutrals100" borderRadius="roundedSquare" px="3" {...boxProps}>
    <Text color="neutrals0" fontSize="s0" fontWeight="semibold">
      {children}
    </Text>
  </Box>
);
